import React, { useEffect, useCallback, useState } from 'react';
import { X, Check, AlertTriangle, Save, Loader2, Ban } from 'lucide-react';
import { ValidationProject, ValidationFlag } from '../../types/validation';
import { updateProject } from '../../services/api';

interface ComparisonModalProps {
  project: ValidationProject;
  onClose: () => void;
  onProjectUpdate?: () => void;
  startInEditMode?: boolean;
}

interface ComparisonField {
  name: string;
  key: 'basin' | 'volumetricWaterBenefit' | 'costShare' | 'quantificationMethod' | 'benefitEndDate';
  factSheet: string;
  system: string;
  match: boolean;
}

const ComparisonModal: React.FC<ComparisonModalProps> = ({ project, onClose, onProjectUpdate, startInEditMode = false }) => {
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Helper to find a flag related to a given field key
  const findFlagForField = useCallback((fieldKey: string): ValidationFlag | undefined => {
    const fieldMapping: Record<string, string[]> = {
      volumetricWaterBenefit: ['benefitVolume', 'volumetricWaterBenefit'],
      costShare: ['costShare'],
      basin: ['basin', 'location'],
      quantificationMethod: ['quantificationMethod'],
      benefitEndDate: ['benefitDuration', 'benefitEndDate'],
    };
    const matchFields = fieldMapping[fieldKey] || [fieldKey];
    return project.flags.find(f => matchFields.includes(f.field));
  }, [project.flags]);

  // Initialize editedData, suggesting flag.expectedValue where available
  const [editedData, setEditedData] = useState(() => {
    const initial: Record<string, string> = {
      basin: project.details.basin,
      volumetricWaterBenefit: project.details.volumetricWaterBenefit,
      costShare: project.details.costShare,
      quantificationMethod: project.details.quantificationMethod,
      benefitEndDate: project.details.benefitEndDate || '',
    };
    for (const flag of project.flags) {
      if (flag.expectedValue) {
        if (['benefitVolume', 'volumetricWaterBenefit'].includes(flag.field)) {
          initial.volumetricWaterBenefit = flag.expectedValue;
        } else if (flag.field === 'costShare') {
          initial.costShare = flag.expectedValue;
        } else if (['benefitDuration', 'benefitEndDate'].includes(flag.field)) {
          initial.benefitEndDate = flag.expectedValue;
        }
      }
    }
    return initial;
  });

  // Handle escape key to close modal
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [handleKeyDown]);

  // Generate comparison fields — derive mismatch state from flags, not hardcoded IDs
  const fieldDefs: { name: string; key: ComparisonField['key']; value: string }[] = [
    { name: 'Location/Basin', key: 'basin', value: project.details.basin },
    { name: 'Volumetric Water Benefit', key: 'volumetricWaterBenefit', value: project.details.volumetricWaterBenefit },
    { name: 'Cost-Share', key: 'costShare', value: project.details.costShare },
    { name: 'Quantification Method', key: 'quantificationMethod', value: project.details.quantificationMethod },
    { name: 'Benefit Claim End Date', key: 'benefitEndDate', value: project.details.benefitEndDate || '' },
  ];

  const fields: ComparisonField[] = fieldDefs.map(def => {
    const flag = findFlagForField(def.key);
    if (flag) {
      return {
        name: def.name,
        key: def.key,
        factSheet: flag.factSheetValue || flag.systemValue || def.value,
        system: flag.expectedValue || def.value,
        match: false,
      };
    }
    return {
      name: def.name,
      key: def.key,
      factSheet: def.value,
      system: def.value,
      match: true,
    };
  });

  const hasMismatch = fields.some(f => !f.match);

  // Projects always transition to clean (eligible) when resolved
  const getNextStatus = (): 'clean' => {
    return 'clean';
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const nextStatus = getNextStatus();
      await updateProject(project.id, {
        ...editedData,
        status: nextStatus,
        flags: [] as ValidationFlag[],
      });
      onProjectUpdate?.();
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkNonEligible = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await updateProject(project.id, {
        status: 'non-eligible' as 'clean' | 'stage1' | 'stage2',
        flags: project.flags,
      });
      onProjectUpdate?.();
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to mark as non-eligible');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (key: string, value: string) => {
    setEditedData(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="comparison-modal-title"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 id="comparison-modal-title" className="text-xl font-bold text-gray-900">
              {isEditing ? 'Edit Project Data' : 'System vs Fact Sheet Comparison'}
            </h2>
            <p className="text-gray-600 mt-1">{project.name}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                project.status === 'stage1' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
              }`}>
                {project.status === 'stage1' ? 'Stage 1' : 'Stage 2'}
              </span>
              {isEditing && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                  Editing Mode
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Note */}
          <div className="p-4 bg-blue-50 rounded-lg" role="note">
            <p className="text-sm text-blue-700">
              {isEditing ? (
                <>
                  <strong>Edit Mode:</strong> Update the values below to correct any discrepancies.
                  Saving will mark the project as Eligible.
                </>
              ) : (
                <>
                  <strong>Automated Comparison Process:</strong> This comparison uses OCR and NLP to extract structured data from project fact sheets and validates it against system entries. Mismatches are automatically flagged for review.
                </>
              )}
            </p>
          </div>

          {/* Error message */}
          {saveError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{saveError}</p>
            </div>
          )}

          {/* Comparison/Edit Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Fact Sheet Column */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Fact Sheet (Extracted)</h3>
              <div className="space-y-3" role="list" aria-label="Fact sheet values">
                {fields.map((field, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${field.match ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}
                    role="listitem"
                  >
                    <div className="text-xs text-gray-500 uppercase">{field.name}</div>
                    <div className="text-sm font-medium text-gray-900 mt-1">{field.factSheet}</div>
                    <div className={`flex items-center gap-1 mt-2 text-xs ${field.match ? 'text-green-600' : 'text-red-600'}`}>
                      {field.match ? <Check className="w-3 h-3" aria-hidden="true" /> : <AlertTriangle className="w-3 h-3" aria-hidden="true" />}
                      <span>{field.match ? 'Match' : 'Mismatch'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* System Column (Editable when in edit mode) */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {isEditing ? 'System Data (Editable)' : 'System Data'}
              </h3>
              <div className="space-y-3" role="list" aria-label="System data values">
                {fields.map((field, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      isEditing
                        ? 'border-cyan-200 bg-cyan-50'
                        : field.match ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                    }`}
                    role="listitem"
                  >
                    <div className="text-xs text-gray-500 uppercase">{field.name}</div>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData[field.key]}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        className="w-full mt-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    ) : (
                      <>
                        <div className="text-sm font-medium text-gray-900 mt-1">{field.system}</div>
                        <div className={`flex items-center gap-1 mt-2 text-xs ${field.match ? 'text-green-600' : 'text-red-600'}`}>
                          {field.match ? <Check className="w-3 h-3" aria-hidden="true" /> : <AlertTriangle className="w-3 h-3" aria-hidden="true" />}
                          <span>{field.match ? 'Match' : 'Mismatch'}</span>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Flags Section */}
          {project.flags.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Flags</h3>
              {project.flags.map((flag, index) => (
                <div key={index} className={`p-4 rounded-lg border-l-4 ${
                  project.status === 'stage2' ? 'bg-red-50 border-red-500' : 'bg-amber-50 border-amber-500'
                } mb-3`}>
                  <h4 className="font-semibold text-gray-900">{flag.type}</h4>
                  <p className="text-sm text-gray-600 mt-1">{flag.description}</p>
                </div>
              ))}
            </div>
          )}

          {/* Action Required */}
          {hasMismatch && !isEditing && (
            <div className="p-4 rounded-lg bg-amber-50 border-l-4 border-amber-500" role="alert">
              <h4 className="font-semibold text-amber-800">Action Required</h4>
              <p className="text-sm text-amber-700 mt-1">
                Discrepancies detected between fact sheet and system data. Click "Edit & Resolve" to correct values and advance the project.
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleMarkNonEligible}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <Ban className="w-4 h-4" />
            Mark Non-Eligible
          </button>

          <div className="flex gap-3">
            {isEditing ? (
              <>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditedData({
                      basin: project.details.basin,
                      volumetricWaterBenefit: project.details.volumetricWaterBenefit,
                      costShare: project.details.costShare,
                      quantificationMethod: project.details.quantificationMethod,
                      benefitEndDate: project.details.benefitEndDate || '',
                    });
                  }}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save & Advance
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors"
                >
                  Edit & Resolve
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonModal;
